/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { EvidenceAnswer, EvidenceGroup } from '@app/app/page-modules/course/models/course-assignments.model';
import { EditorContent } from '@app/app/shared/models/editor/editor-content.model';
import {
  EvidenceBlockFormContent,
  EvidenceFormContent,
  EvidenceMediaType,
  FormContent,
  FrameworkEvidenceDomain,
  FrameworkEvidenceFormContent,
  OpenEvidenceFormContent,
} from '@app/app/shared/models/editor/form-content.model';

export type EvidenceBlockType = EvidenceBlockFormContent['type'];

export const EVIDENCE_BLOCK_TYPES: readonly string[] = ['EVIDENCE', 'OPEN_EVIDENCE', 'FRAMEWORK_EVIDENCE'] satisfies EvidenceBlockType[];

export const EVIDENCE_MEDIA_TYPES: readonly EvidenceMediaType[] = ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT'];

export function isEvidenceBlockType(type: string | null | undefined): type is EvidenceBlockType {
  return !!type && EVIDENCE_BLOCK_TYPES.includes(type);
}

export function isEvidenceBlock(block: EditorContent): block is EditorContent & { uid: string; content: EvidenceBlockFormContent } {
  return block.type === 'FORM' && !!block.uid && isEvidenceBlockType((block.content as FormContent | undefined)?.type);
}

/** The section holds at least one evidence block, which makes its task a portfolio rather than a workbook. */
export function hasEvidenceBlock(content: EditorContent[] | null | undefined): boolean {
  return (content ?? []).some(isEvidenceBlock);
}

/** The framework tags the section's evidence blocks carry: tagged slots and framework domains. */
export function evidenceTagIds(content: EditorContent[] | null | undefined): string[] {
  return (content ?? []).filter(isEvidenceBlock).flatMap((block) => {
    const config = block.content as EvidenceFormContent | FrameworkEvidenceFormContent;
    if (config.type === 'FRAMEWORK_EVIDENCE') {
      return (config.domains ?? []).flatMap((domain) => (domain.tagId ? [domain.tagId] : []));
    }
    return config.type === 'EVIDENCE' && config.tagId ? [config.tagId] : [];
  });
}

/**
 * What a section's evidence makes its task: a framework portfolio when it holds a framework block, a portfolio when it
 * holds any other evidence block, and nothing (a workbook) otherwise.
 */
export type SectionEvidenceKind = 'FRAMEWORK' | 'PORTFOLIO' | null;

export function sectionEvidenceKind(content: EditorContent[] | null | undefined): SectionEvidenceKind {
  const blocks = (content ?? []).filter(isEvidenceBlock);
  if (blocks.some((block) => block.content.type === 'FRAMEWORK_EVIDENCE')) {
    return 'FRAMEWORK';
  }
  return blocks.length ? 'PORTFOLIO' : null;
}

/** The block's config with every field present, whatever the stored node left out. */
export function evidenceConfig(content: Partial<EvidenceFormContent>): EvidenceFormContent {
  return {
    ...(content as EvidenceFormContent),
    type: 'EVIDENCE',
    title: content.title ?? '',
    required: content.required ?? true,
    allowedMediaTypes: content.allowedMediaTypes ?? [],
    requireDescription: content.requireDescription ?? false,
    minItems: content.minItems ?? 1,
    maxItems: content.maxItems ?? null,
    frameworkUid: content.frameworkUid ?? null,
    tagId: content.tagId ?? null,
  };
}

export function openEvidenceConfig(content: Partial<OpenEvidenceFormContent>): OpenEvidenceFormContent {
  return {
    ...(content as OpenEvidenceFormContent),
    type: 'OPEN_EVIDENCE',
    title: content.title ?? '',
    required: content.required ?? true,
    allowedMediaTypes: content.allowedMediaTypes ?? [],
    requireDescription: content.requireDescription ?? false,
    minGroups: content.minGroups ?? 1,
    minItemsPerGroup: content.minItemsPerGroup ?? 1,
    maxGroups: content.maxGroups ?? null,
  };
}

export function frameworkEvidenceConfig(content: Partial<FrameworkEvidenceFormContent>): FrameworkEvidenceFormContent {
  return {
    ...(content as FrameworkEvidenceFormContent),
    type: 'FRAMEWORK_EVIDENCE',
    title: content.title ?? '',
    required: content.required ?? true,
    allowedMediaTypes: content.allowedMediaTypes ?? [],
    requireDescription: content.requireDescription ?? false,
    frameworkUid: content.frameworkUid ?? null,
    domains: (content.domains ?? []).map(frameworkDomain),
    allowLearnerDomains: content.allowLearnerDomains ?? false,
  };
}

/** A domain with every field present. */
export function frameworkDomain(domain: Partial<FrameworkEvidenceDomain>): FrameworkEvidenceDomain {
  return {
    uid: domain.uid ?? '',
    tagId: domain.tagId ?? null,
    name: domain.name ?? '',
    guidance: domain.guidance ?? '',
    minItems: domain.minItems ?? 1,
    required: domain.required ?? true,
    allowedMediaTypes: domain.allowedMediaTypes ?? [],
  };
}

/** The media types a group of the block takes: a framework domain's own, when it narrows them, or else the block's. */
export function groupMediaTypes(config: EvidenceBlockFormContent, groupUid: string): EvidenceMediaType[] {
  if (config.type === 'FRAMEWORK_EVIDENCE') {
    const domain = (config as FrameworkEvidenceFormContent).domains.find((each) => each.uid === groupUid);
    if (domain?.allowedMediaTypes.length) {
      return domain.allowedMediaTypes;
    }
  }
  return config.allowedMediaTypes;
}

/**
 * The block's answer, decoded. A blank or unreadable answer is an empty one. An `EVIDENCE` block always has exactly
 * its own group, so a learner who has placed nothing still has somewhere to place the first item. A framework block
 * always has one group per domain, in the author's order, followed by the learner's own domains in theirs, while the
 * author allows them.
 */
export function parseEvidenceAnswer(
  answer: string | null | undefined,
  block: {
    uid: string;
    type: string;
    domains?: readonly { uid: string; tagId?: string | null }[];
    allowLearnerDomains?: boolean;
  },
): EvidenceAnswer {
  let groups: EvidenceGroup[] = [];
  if (answer?.trim()) {
    try {
      const parsed = JSON.parse(answer) as Partial<EvidenceAnswer> | null;
      groups = Array.isArray(parsed?.groups)
        ? parsed.groups
            .filter((group): group is EvidenceGroup => !!group && typeof group.uid === 'string')
            .map((group) => ({
              ...group,
              learningExperienceIds: Array.isArray(group.learningExperienceIds) ? group.learningExperienceIds : [],
            }))
        : [];
    } catch {
      groups = [];
    }
  }
  if (block.type === 'EVIDENCE') {
    // Whatever else the stored group carries (its `tagId`) goes back with the next write.
    const own = groups.find((group) => group.uid === block.uid);
    return { groups: [{ ...own, uid: block.uid, learningExperienceIds: own?.learningExperienceIds ?? [] }] };
  }
  if (block.type === 'FRAMEWORK_EVIDENCE') {
    const domains = block.domains ?? [];
    const domainUids = new Set(domains.map((domain) => domain.uid));
    // A group for a domain the author has since removed has nowhere to go, as on the server: it is left out.
    const learner = groups.filter((group) => !domainUids.has(group.uid) && !!group.tagId);
    // A learner's domain for a tag the author has since given a domain of its own folds into it, keeping what was placed
    // and leaving one domain per tag, which is all the server takes.
    const folded = (tagId: string | null | undefined): EvidenceGroup[] => (tagId ? learner.filter((group) => group.tagId === tagId) : []);
    const teacherTags = new Set(domains.flatMap((domain) => (domain.tagId ? [domain.tagId] : [])));
    // A domain's name and tag are the author's, on the node; only the learner's own domains carry theirs.
    const teacher = domains.map((domain) => {
      const own = groups.find((group) => group.uid === domain.uid)?.learningExperienceIds ?? [];
      const extra = folded(domain.tagId).flatMap((group) => group.learningExperienceIds);
      return { uid: domain.uid, learningExperienceIds: [...new Set([...own, ...extra])] };
    });
    // Once the author stops taking learners' own domains, what is left of them is dropped, as the server does on save.
    const own = block.allowLearnerDomains ? learner.filter((group) => !teacherTags.has(group.tagId as string)) : [];
    return { groups: [...teacher, ...own] };
  }
  return { groups };
}

/** How far a group is toward what its block asks of it. `target` is 0 when nothing is asked. */
export interface EvidenceGroupMeter {
  uid: string;
  items: number;
  target: number;
  met: boolean;
}

export interface EvidenceBlockMeter {
  groups: EvidenceGroupMeter[];
  /** `OPEN_EVIDENCE`: how many groups the learner has, against the minimum. */
  groupCount: number;
  minGroups: number;
  /** Items placed without the description the block requires. */
  missingDescriptions: number;
  /** The block counts as done: optional, or every rule met. Mirrors the server's completion rule. */
  satisfied: boolean;
}

/**
 * The block's completion, worked out the way the server does it, so the learner sees it move as they place evidence.
 * Items whose learning experience isn't known (deleted since) don't count. `described` says whether an item has the
 * description a `requireDescription` block asks for.
 */
export function evidenceBlockMeter(
  config: EvidenceBlockFormContent,
  answer: EvidenceAnswer,
  known: (learningExperienceId: string) => boolean,
  described: (learningExperienceId: string) => boolean,
): EvidenceBlockMeter {
  const counted = (group: EvidenceGroup): string[] => group.learningExperienceIds.filter(known);
  const targetOf = groupTarget(config);
  const groups = answer.groups.map((group) => {
    const items = counted(group).length;
    const target = targetOf(group.uid);
    return { uid: group.uid, items, target, met: items >= target };
  });
  const missingDescriptions = config.requireDescription
    ? answer.groups.reduce((sum, group) => sum + counted(group).filter((id) => !described(id)).length, 0)
    : 0;
  const minGroups =
    config.type === 'OPEN_EVIDENCE' && config.required ? Math.max(0, (config as OpenEvidenceFormContent).minGroups ?? 0) : 0;
  const satisfied =
    !config.required || (groups.every((group) => group.met) && answer.groups.length >= minGroups && missingDescriptions === 0);
  return { groups, groupCount: answer.groups.length, minGroups, missingDescriptions, satisfied };
}

/** What a group must hold for its block to be done: nothing on an optional block, an optional domain or a learner's domain. */
function groupTarget(config: EvidenceBlockFormContent): (groupUid: string) => number {
  if (!config.required) {
    return () => 0;
  }
  switch (config.type) {
    case 'EVIDENCE':
      return () => Math.max(0, (config as EvidenceFormContent).minItems ?? 0);
    case 'OPEN_EVIDENCE':
      return () => Math.max(0, (config as OpenEvidenceFormContent).minItemsPerGroup ?? 0);
    case 'FRAMEWORK_EVIDENCE': {
      const domains = new Map((config as FrameworkEvidenceFormContent).domains.map((domain) => [domain.uid, domain]));
      return (groupUid) => {
        const domain = domains.get(groupUid);
        return domain?.required ? Math.max(0, domain.minItems ?? 0) : 0;
      };
    }
  }
}

/**
 * Whether a learning experience's description counts as one for `requireDescription`, as the server decides it: some
 * non-blank text, or any block that isn't text (an image, an embed, …).
 */
export function hasEvidenceDescription(description: EditorContent[] | null | undefined): boolean {
  return (description ?? []).some((block) => {
    if (block.type !== 'PARAGRAPH') {
      return true;
    }
    const html = String(block.content ?? '');
    return (
      html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim().length > 0
    );
  });
}
